"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth, useUser } from "@clerk/nextjs";

import {
  trackBroadcastAskOpened,
  trackBroadcastPanelSeen,
  trackBroadcastSent,
} from "@/lib/analytics";
import { MioAuthDialog } from "@/components/auth/MioAuthDialog";
import type { AuthMode } from "@/components/auth/useAuthFlow";
import { WatchLimitDialog } from "@/components/watches/WatchLimitDialog";
import {
  ClaimsApiError,
  createBroadcast,
  previewClaim,
  type BroadcastTarget,
  type ClaimPreviewResponse,
} from "@/lib/api/claims";
import type { ProductPageData } from "@/lib/api/types";
import {
  WatchesApiError,
  createWatch,
  deleteWatch,
  listWatches,
  type WatchTargetSpec,
} from "@/lib/api/watches";
import {
  advertisedPrice,
  buildPayload,
  computeNegState,
  type NegReady,
} from "@/lib/negotiation/payload";
import { currentEvidenceUrl, realName } from "@/lib/negotiation/user";
import { prettifyRetailerSlug } from "@/lib/retailers/prettify";

import { EmailPreviewModal, type AskableShop } from "./EmailPreviewModal";

/**
 * AskPanel - the ONE broadcast button under the comparison winner
 * (broadcast Day 5).
 *
 * Replaces the per-shop Negotiate buttons with a single group ask: one
 * click asks the best reachable shops to beat the winner, in the user's
 * name, over a 48h window. Promoted from the approved compare mockup
 * (web/app/mockup/compare/page.tsx AskPanel), now wired to real data:
 *
 *   - The askable set is every row where computeNegState(...).kind ===
 *     "ready" (in stock, has a cheaper advertised competitor, negotiable),
 *     cheapest-advertised first. The winner is the BENCHMARK the others are
 *     asked to beat and is never asked itself (nobody is cheaper than it).
 *   - Empty set (single-shop page, or nobody cheaper) => renders nothing,
 *     Buy-only, exactly like today where computeNegState hides every button.
 *   - The picker opens with the best DEFAULT_ASK ticked; the user can add
 *     up to MAX_ASK (the server also hard-caps at broadcast_max_shops).
 *   - On send, one claim body per ticked shop (buildPayload, shared with
 *     the single-shop button) + that shop's price breakdown, then
 *     createBroadcast(targets, 48, token).
 *
 * Watch the price (secondary action): the approved mockup pairs a Watch
 * toggle with the primary ask, so this panel wires the existing watches API
 * back in - listWatches on mount to detect an existing watch, createWatch to
 * start one (NO target price: alert on any drop, matching the mockup's "new
 * low since you started" rule), deleteWatch to stop. Rendered only when
 * watchTargets is supplied by the insertion site. This is the mockup's
 * toggle UX, NOT the old WatchButton's target-price prompt + navigate-away.
 *
 * Gated by NEXT_PUBLIC_BROADCAST_ENABLED at the INSERTION site (Day 6);
 * this component assumes it is only mounted when the flag is on.
 */

const DEFAULT_ASK = 6;
const MAX_ASK = 12;

const ACCENT = "#f6c453";
// Comparison-page green that flips with the theme: deep readable green on the
// light canvas (#0B7A46, AA), the original bright #4ade80 on dark. Defined by
// .cmp-surface in globals.css (the surface this panel always renders inside).
const GOOD = "var(--cmp-good)";
const FG = "hsl(var(--foreground))";
const FG2 = "hsl(var(--muted-foreground))";
const FG3 = "hsl(var(--muted-foreground))"; // 2026-07-29 a11y: was #8b8b94 (5.78:1); 10.81:1 on the dark panel, matches the app-wide muted tier
const LINE_STRONG = "hsl(var(--border))";

const AUD2 = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
function money0(n: number): string {
  return "$" + n.toLocaleString("en-AU", { maximumFractionDigits: 0 });
}

interface Ask {
  row: ProductPageData;
  ready: NegReady;
  adv: number;
}

export function AskPanel({
  allRows,
  watchTargets,
  postcode,
  priceCheck,
  pageSlug,
}: {
  /** B-246 (2026-09-19): the /c/<slug> this panel sits on (or the slug the
   *  live search published), so an existing watch is recognised by PAGE.
   *  The title match stays as the fallback for watches without a page. */
  pageSlug?: string | null;
  /** The full offer group for this comparison. The winner (allRows[0] - the
   * same product Hero renders as "best deal") is the benchmark the group is
   * asked to beat, and the price shown in the panel copy. */
  allRows: ProductPageData[];
  /** The comparison's watch targets (same list `<Hero>` receives), so the
   * Watch toggle can create a watch. When absent, Watch is hidden. */
  watchTargets?: WatchTargetSpec[];
  /** 2026-09-17 (owner-approved): the page's price-check data (the
   *  "Typical price" strip above this panel). Only used to word the watch
   *  button: "Tell me if it drops again" when today already is the lowest
   *  tracked, else "Tell me if it drops below $X". Absent on the live
   *  search page, where the plain wording is used. Display only. */
  priceCheck?: { today?: number; lowest?: number; verdict?: string } | null;
  /** Delivery postcode for the watch's initial scan. */
  postcode?: string;
}) {
  // The winner = the first row, exactly what Hero renders as the "best
  // deal" (Hero reads `group.products[0]`). Derived here so the panel's
  // benchmark price can never disagree with the winner card above it.
  const winner = allRows[0];
  const { isSignedIn, user } = useUser();
  const { userId, getToken } = useAuth();
  const pathname = usePathname() || "";

  // The askable set: rows that are "ready" to negotiate, cheapest-advertised
  // first. Same gating as the single-shop button, so the two never disagree.
  const asks = useMemo<Ask[]>(() => {
    return allRows
      .map((row) => ({ row, neg: computeNegState(row, allRows) }))
      .filter(
        (x): x is { row: ProductPageData; neg: NegReady } =>
          x.neg.kind === "ready",
      )
      .map((x) => ({ row: x.row, ready: x.neg, adv: advertisedPrice(x.row) }))
      .sort((a, b) => a.adv - b.adv);
  }, [allRows]);

  const [touched, setTouched] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("sign-in");
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState<ClaimPreviewResponse | null>(null);
  const [sending, setSending] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [idleError, setIdleError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ shops: number } | null>(null);

  // Watch toggle state. watchId is the live watch's id when watching, null
  // when not; watchChecked flips true once we've asked the server.
  const [watchId, setWatchId] = useState<string | null>(null);
  const [watchBusy, setWatchBusy] = useState(false);
  const [watchChecked, setWatchChecked] = useState(false);
  // 2026-09-17 (owner-approved): a signed-out tap on the watch button is
  // remembered here, so the watch is created by itself the moment sign-up
  // or sign-in completes (the effect below). Before, the dialog closed and
  // the person had to find the button and tap it a second time.
  const pendingWatch = useRef(false);
  const [watchError, setWatchError] = useState<string | null>(null);
  // 2026-09-03: the 20-watch cap opens the shared pop-up instead of an
  // inline message (owner-approved mockup 1180c1bb).
  const [limitOpen, setLimitOpen] = useState(false);

  const canWatch = !!watchTargets && watchTargets.length > 0;

  // Keep the default ticked set (the best DEFAULT_ASK) synced to the askable
  // shops while the comparison streams in, UNTIL the user touches the picker.
  useEffect(() => {
    if (touched) return;
    setExcluded(
      new Set(asks.slice(DEFAULT_ASK).map((a) => a.row.retailer_slug)),
    );
  }, [asks, touched]);

  // Funnel: the panel became visible (fires once per view).
  const seenRef = useRef(false);
  useEffect(() => {
    if (!seenRef.current && asks.length > 0) {
      seenRef.current = true;
      trackBroadcastPanelSeen({ shopCount: asks.length, pathname });
    }
  }, [asks.length, pathname]);

  // Detect an existing watch for this comparison so the toggle reflects
  // reality (match by label - the only key listWatches returns). One free
  // read once the user is signed in; guarded so it runs a single time.
  // 2026-09-17: the remembered tap. Waits for the existing-watch check
  // (watchChecked) so a person who already watches this product is never
  // given a second watch; then creates exactly once and forgets the intent.
  useEffect(() => {
    if (!pendingWatch.current || !isSignedIn || !watchChecked) return;
    pendingWatch.current = false;
    if (watchId) return;
    void createWatchNow();
    // createWatchNow reads the latest props/state at call time; it is not a
    // dependency by design (a stable ref would re-run on every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, watchChecked, watchId]);

  useEffect(() => {
    if (watchChecked || !isSignedIn || !canWatch || !winner) return;
    let cancelled = false;
    (async () => {
      try {
        const token = (await getToken()) ?? "";
        const watches = await listWatches(token);
        if (cancelled) return;
        // B-246: by page first (the title changes whenever the winner
        // changes, which is why "Watching" vanished on 5 of 9 watches), by
        // title as the fallback for a watch that has no page yet.
        const mine =
          (pageSlug ? watches.find((w) => w.comparison_slug === pageSlug) : undefined) ??
          watches.find((w) => w.label === winner.product_name);
        setWatchId(mine ? mine.id : null);
      } catch {
        // Non-fatal: leave the toggle in its default (not watching).
      } finally {
        if (!cancelled) setWatchChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [watchChecked, isSignedIn, canWatch, getToken, winner, pageSlug]);

  // The cheapest ticked shop stands in for the group in the popup preview.
  // Recompute its slug each render so the effect below can re-compose the
  // sample when the representative changes (e.g. the user unticks the shop
  // currently shown). null only when nothing is ticked.
  const repSlug =
    asks.find((a) => !excluded.has(a.row.retailer_slug))?.row.retailer_slug ??
    null;

  // Keep the preview sample in sync with the CURRENT representative shop.
  // The popup shows ONE sample email, composed for the cheapest ticked shop.
  // If the user unticks that shop in the picker the representative changes, so
  // the sample must be re-composed for the new shop; otherwise the popup keeps
  // showing the removed shop's email. (The send always composes per ticked
  // shop, so only the preview was ever affected - the bug the owner hit.)
  // Fires on open and on every representative change. Each distinct shop's
  // sample is cached server-side, so toggling back to a shop already shown is
  // free (no extra LLM call).
  useEffect(() => {
    if (!modalOpen || !repSlug || !user) return;
    const userName = realName(user);
    if (!userName) return;
    const rep = asks.find((a) => a.row.retailer_slug === repSlug);
    if (!rep) return;
    let cancelled = false;
    setPreview(null); // shimmer while the new sample is written
    setModalError(null);
    (async () => {
      try {
        const body = buildPayload({
          product: rep.row,
          ready: rep.ready,
          allRows,
          userName,
          userEmail: user.primaryEmailAddress?.emailAddress ?? "",
          evidenceUrl: currentEvidenceUrl(),
        });
        const pv = await previewClaim(body, (await getToken()) ?? "");
        if (!cancelled) setPreview(pv);
      } catch (e) {
        if (!cancelled) setModalError(humanError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // modalOpen + repSlug are the real triggers; asks/allRows/user are stable
    // for the modal session and would only cause redundant re-composes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, repSlug]);

  // No winner yet (still streaming) => render nothing. An empty ready set no
  // longer bails here: even when NO shop can be asked to beat the price (e.g.
  // the only negotiable shop is already the cheapest on advertised price, as
  // on a Dyson page), the user must still be able to WATCH the price - Watch
  // is independent of whether negotiation is possible. The watch-only panel is
  // rendered further down when asks is empty.
  if (!winner) return null;

  const ticked = asks.filter((a) => !excluded.has(a.row.retailer_slug));
  const tickedCount = ticked.length;
  const askableTotal = asks.length;
  const benchmarkPrice = parseFloat(winner.delivered_total);
  const benchmarkFinite = Number.isFinite(benchmarkPrice);
  const benchmarkName = prettifyRetailerSlug(winner.retailer_slug);
  const benchmarkMoney = benchmarkFinite
    ? AUD2.format(benchmarkPrice)
    : "the best price";

  const askableShops: AskableShop[] = asks.map((a) => ({
    slug: a.row.retailer_slug,
    name: prettifyRetailerSlug(a.row.retailer_slug),
  }));

  function toggleShop(slug: string) {
    setTouched(true);
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        // Re-include this shop, capped at MAX_ASK ticked.
        const currentTicked = asks.filter(
          (a) => !next.has(a.row.retailer_slug),
        ).length;
        if (currentTicked >= MAX_ASK) return prev;
        next.delete(slug);
      } else {
        // Exclude, but never below one ticked shop: there must always be at
        // least one shop to ask, and the preview needs a representative.
        const currentTicked = asks.filter(
          (a) => !next.has(a.row.retailer_slug),
        ).length;
        if (currentTicked <= 1) return prev;
        next.add(slug);
      }
      return next;
    });
  }

  async function onGetPrice() {
    setIdleError(null);
    // R7/R8: the emails are sent AS the user, so a real identity is required.
    if (!isSignedIn || !user || !userId) {
      setAuthMode("sign-in");
      setAuthOpen(true);
      return;
    }
    const userName = realName(user);
    if (!userName) {
      setIdleError("Add your name in your account settings to ask for prices.");
      return;
    }
    // Open the popup INSTANTLY with a shimmer; write the sample email behind
    // it (the same instant-open pattern as the single-shop button).
    setModalError(null);
    setPreview(null);
    setModalOpen(true);
    trackBroadcastAskOpened({ shopCount: tickedCount, pathname });
    // The preview is composed by the representative-sync effect (it fires when
    // modalOpen flips true), so it also re-composes if the user then unticks
    // the shop being shown.
  }

  async function onWatch() {
    setWatchError(null);
    // Watch is a signed-in action (spec 4). Anonymous -> auth dialog, then
    // the user clicks again (same as the standalone WatchButton).
    if (!isSignedIn) {
      // 2026-09-17: a new visitor lands on the sign-up screen (the foot
      // link still offers sign-in); the tap is remembered and the watch is
      // created automatically once they are in.
      pendingWatch.current = true;
      setAuthMode("sign-up");
      setAuthOpen(true);
      return;
    }
    if (!canWatch) return;
    if (watchId) {
      setWatchBusy(true);
      try {
        const token = (await getToken()) ?? "";
        await deleteWatch(watchId, token);
        setWatchId(null);
      } catch (e) {
        setWatchError(
          e instanceof WatchesApiError
            ? `Couldn't update your watch (${e.status}).`
            : "Couldn't update your watch right now.",
        );
      } finally {
        setWatchBusy(false);
      }
      return;
    }
    await createWatchNow();
  }

  // The create path, shared by the tap and by the remembered-intent effect.
  async function createWatchNow() {
    if (!canWatch || !winner) return;
    setWatchBusy(true);
    try {
      const token = (await getToken()) ?? "";
      const w = await createWatch(
        {
          label: winner.product_name,
          targets: watchTargets!,
          user_postcode: postcode,
          country_code: "AU",
          // Seed from the price the page already shows -> instant create,
          // no synchronous re-crawl on the click.
          baseline_true_final_price_aud: winner.delivered_total,
          current_best_retailer_slug: winner.retailer_slug,
        },
        token,
      );
      setWatchId(w.id);
    } catch (e) {
      if (e instanceof WatchesApiError && e.isWatchLimit()) {
        setLimitOpen(true);
      } else {
        setWatchError(
          e instanceof WatchesApiError
            ? `Couldn't update your watch (${e.status}).`
            : "Couldn't update your watch right now.",
        );
      }
    } finally {
      setWatchBusy(false);
    }
  }

  async function onSend() {
    if (!user) return;
    const userName = realName(user);
    setSending(true);
    setModalError(null);
    try {
      const token = (await getToken()) ?? "";
      const chosen = asks.filter((a) => !excluded.has(a.row.retailer_slug));
      const targets: BroadcastTarget[] = chosen.map((a) => ({
        ...buildPayload({
          product: a.row,
          ready: a.ready,
          allRows,
          userName,
          userEmail: user.primaryEmailAddress?.emailAddress ?? "",
          evidenceUrl: currentEvidenceUrl(),
        }),
        // Snapshot this shop's TFP breakdown so the requests screen can
        // rebuild the ledger with the agreed price later (Day 7). Omit when
        // empty so the column stays null rather than "[]".
        price_components: a.row.price_components?.length
          ? a.row.price_components
          : undefined,
      }));
      const res = await createBroadcast(targets, 48, token);
      const sentCount = res.results.filter((r) => r.status === "sent").length;
      if (sentCount === 0) {
        // Honest: nothing actually went out (all shops needed manual / errored).
        setModalError(
          "None of these shops could be reached automatically just now. Please try again in a moment.",
        );
        return;
      }
      trackBroadcastSent({ shopCount: sentCount, pathname });
      setModalOpen(false);
      setSent({ shops: sentCount });
    } catch (e) {
      setModalError(humanError(e));
    } finally {
      setSending(false);
    }
  }

  // The Watch toggle, rendered the same everywhere except its emphasis:
  //   primary=true  -> amber main CTA (used when Watch is the ONLY action, so
  //                    it stands out instead of looking like a weak outline)
  //   primary=false -> ghost secondary (sits beside the amber "Get my best
  //                    price" in the full panel)
  // Once watching, both variants switch to the green "confirmed" state.
  // 2026-09-17 (owner-approved wording): the button names the number the
  // watch really acts on - today's best price (the alert fires on a drop
  // from the price the watcher was last told). When the price strip says
  // today already is the lowest tracked, "drops again" says the same thing
  // without repeating the number.
  const todayIsTheLow =
    !!priceCheck &&
    (priceCheck.verdict === "best_yet" ||
      (typeof priceCheck.today === "number" &&
        typeof priceCheck.lowest === "number" &&
        priceCheck.today <= priceCheck.lowest));
  const watchLabel = todayIsTheLow
    ? "Tell me if it drops again"
    : benchmarkFinite
      ? `Tell me if it drops below ${money0(benchmarkPrice)}`
      : "Tell me when it drops";

  function watchBtn(primary: boolean | "strong") {
    if (!canWatch) return null;
    const watching = !!watchId;
    const variant = watching
      ? {
          border: "1px solid rgba(74,222,128,0.35)",
          background: "rgba(74,222,128,0.08)",
          color: GOOD,
          boxShadow: "none",
          padding: "12px 18px",
          fontSize: "var(--fs-small)",
        }
      : primary === "strong"
        ? {
            // 2026-09-17 (owner pick, option 6): the teal watch colour, in the
            // same gradient form as the page's chips (globals.css --chip-watch-*).
            border: 0,
            background: "var(--chip-watch-bg)",
            color: "var(--chip-watch-fg)",
            boxShadow: "var(--chip-watch-glow), inset 0 1px 0 rgba(255,255,255,0.22)",
            padding: "12px 22px",
            fontSize: "var(--fs-body)",
          }
      : primary
        ? {
            border: 0,
            background: "linear-gradient(180deg, #f6c453 0%, #e9b143 100%)",
            color: "#1a1306",
            boxShadow:
              "0 8px 24px -8px rgba(246,196,83,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
            padding: "12px 22px",
            fontSize: "var(--fs-body)",
          }
        : {
            border: `1px solid ${LINE_STRONG}`,
            background: "transparent",
            color: FG2,
            boxShadow: "none",
            padding: "12px 18px",
            fontSize: "var(--fs-small)",
          };
    return (
      <button
        type="button"
        onClick={onWatch}
        disabled={watchBusy}
        className="w-full justify-center sm:w-auto"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          borderRadius: 12,
          fontWeight: 600,
          cursor: watchBusy ? "default" : "pointer",
          opacity: watchBusy ? 0.6 : 1,
          ...variant,
        }}
      >
        <span aria-hidden="true">{watching ? "♥" : "♡"}</span>
        {watchBusy
          ? "Setting up…"
          : watching
            ? "Watching the price"
            : watchLabel}
      </button>
    );
  }

  // ---- watch-only: no shop can be asked, but the price can still be
  //      watched. Render just the Watch control so the option is never lost.
  if (asks.length === 0) {
    if (!canWatch) return null;
    return (
      <>
        <section
          style={{
            marginTop: 20,
            borderRadius: 16,
            border: `1px solid ${LINE_STRONG}`,
            background: "hsl(var(--secondary))",
            padding: "20px 22px",
            backgroundImage:
              "radial-gradient(120% 80% at 0% 0%, rgba(246,196,83,0.07), transparent 60%)",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "1.0625rem",
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: FG,
            }}
          >
            {watchId ? "Watching this price" : "Want it cheaper?"}
          </h2>

          <div
            className="flex-col sm:flex-row"
            style={{
              marginTop: 14,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 12,
            }}
          >
            {watchBtn(true)}
          </div>

          {watchError ? (
            <p
              style={{
                margin: "12px 0 0",
                fontSize: "var(--fs-caption)",
                color: "hsl(var(--destructive))",
                lineHeight: 1.5,
              }}
            >
              {watchError}
            </p>
          ) : null}

          <p
            style={{
              margin: "13px 0 0",
              fontSize: "var(--fs-caption)",
              lineHeight: 1.6,
              color: FG3,
              maxWidth: 560,
            }}
          >
            {watchId ? (
              <>
                We are watching this price for you and will email you if it
                drops below {benchmarkMoney}.
              </>
            ) : (
              <>
                We&apos;ll email you the moment the best price drops below{" "}
                {benchmarkMoney}.
              </>
            )}
          </p>
        </section>

        {authOpen ? (
          <MioAuthDialog
            open={authOpen}
            mode={authMode}
            onOpenChange={setAuthOpen}
            onModeChange={setAuthMode}
          />
        ) : null}
        <WatchLimitDialog open={limitOpen} onOpenChange={setLimitOpen} />
      </>
    );
  }

  // ---- collecting: a broadcast just went out (this session) ------------
  if (sent) {
    return (
      <section
        style={{
          marginTop: 24,
          borderRadius: 20,
          border: "1px solid rgba(74,222,128,0.28)",
          background: "rgba(74,222,128,0.05)",
          padding: "20px 22px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 300px" }}>
          <div
            className="uppercase"
            style={{
              fontSize: "var(--fs-micro)",
              letterSpacing: "0.14em",
              fontWeight: 600,
              color: GOOD,
              marginBottom: 8,
            }}
          >
            Collecting offers
          </div>
          <div style={{ fontSize: "1.0625rem", fontWeight: 600, color: FG }}>
            We have asked {sent.shops} {sent.shops === 1 ? "shop" : "shops"} to
            beat {benchmarkMoney}
          </div>
          <div
            style={{
              fontSize: "var(--fs-small)",
              color: FG3,
              marginTop: 6,
              lineHeight: 1.55,
            }}
          >
            We will email you the moment an offer lands. Offers close in 48
            hours.
          </div>
        </div>
        <a
          href="/requests"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            borderRadius: 12,
            background: "linear-gradient(180deg, #f6c453 0%, #e9b143 100%)",
            color: "#1a1306",
            padding: "11px 18px",
            fontSize: "var(--fs-small)",
            fontWeight: 600,
            textDecoration: "none",
            boxShadow: "0 8px 22px -10px rgba(246,196,83,0.6)",
            flex: "none",
          }}
        >
          See your requests <span aria-hidden="true">→</span>
        </a>
      </section>
    );
  }

  // ---- idle: one question, two answers (ask / watch) -------------------
  return (
    <>
      <section
        style={{
          marginTop: 20,
          borderRadius: 16,
          border: `1px solid ${LINE_STRONG}`,
          background: "hsl(var(--secondary))",
          padding: "20px 22px",
          backgroundImage:
            "radial-gradient(120% 80% at 0% 0%, rgba(246,196,83,0.07), transparent 60%)",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "1.0625rem",
            fontWeight: 600,
            letterSpacing: "-0.015em",
            color: FG,
          }}
        >
          Not happy with{" "}
          {benchmarkFinite ? money0(benchmarkPrice) : "this price"}?
        </h2>

        <div
          className="flex-col sm:flex-row"
          style={{
            marginTop: 14,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onGetPrice}
            className="w-full sm:w-auto"
            style={{
              borderRadius: 12,
              border: 0,
              background: "linear-gradient(180deg, #f6c453 0%, #e9b143 100%)",
              color: "#1a1306",
              padding: "12px 22px",
              fontSize: "var(--fs-body)",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow:
                "0 8px 24px -8px rgba(246,196,83,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
            }}
          >
            Get my best price
          </button>
          {/* 2026-09-17 (owner decision): "Get my best price" keeps the
              amber - it is the differentiator. The watch stands beside it in
              the teal watch colour (globals.css --chip-watch-*) instead of the
              thin grey outline it had. */}
          {watchBtn("strong")}
        </div>

        {idleError || watchError ? (
          <p
            style={{
              margin: "12px 0 0",
              fontSize: "var(--fs-caption)",
              color: "hsl(var(--destructive))",
              lineHeight: 1.5,
            }}
          >
            {idleError ?? watchError}
          </p>
        ) : null}

        <p
          style={{
            margin: "13px 0 0",
            fontSize: "var(--fs-caption)",
            lineHeight: 1.6,
            color: FG3,
            maxWidth: 560,
          }}
        >
          {watchId ? (
            <>
              We are watching this price for you and will email you if it drops
              below {benchmarkMoney}.
            </>
          ) : (
            <>
              We ask the best {tickedCount} of the {askableTotal}{" "}
              {askableTotal === 1 ? "shop" : "shops"} we can reach to beat{" "}
              {benchmarkMoney} at {benchmarkName}, in your name. You choose
              exactly who before anything sends.
              {canWatch
                ? " Or watch the price and we'll email you if it drops."
                : ""}
            </>
          )}
        </p>
      </section>

      {modalOpen ? (
        <EmailPreviewModal
          representativeName={
            ticked[0]
              ? prettifyRetailerSlug(ticked[0].row.retailer_slug)
              : benchmarkName
          }
          preview={preview}
          askableShops={askableShops}
          excluded={excluded}
          toggleShop={toggleShop}
          tickedCount={tickedCount}
          maxAsk={MAX_ASK}
          userName={realName(user)}
          sending={sending}
          error={modalError}
          onSend={onSend}
          onClose={() => setModalOpen(false)}
        />
      ) : null}

      {authOpen ? (
        <MioAuthDialog
          open={authOpen}
          mode={authMode}
          onOpenChange={setAuthOpen}
          onModeChange={setAuthMode}
        />
      ) : null}
      <WatchLimitDialog open={limitOpen} onOpenChange={setLimitOpen} />
    </>
  );
}

function humanError(e: unknown): string {
  if (e instanceof ClaimsApiError) {
    if (e.status === 503) return "Best-price requests aren't switched on yet.";
    if (e.status === 401) return "Please sign in to ask for prices.";
  }
  return "Couldn't send this right now.";
}
